/**
 * The solver's tunable numbers — PLAN §3, steps 2 and 4.
 *
 * Kept in their own module for two reasons. First, they are the only part of
 * the solver a *user* is allowed to change (the advanced drawer), so they need
 * labels, ranges and copy alongside the numbers rather than scattered through
 * the algorithm. Second, they are the part the Swift port is most likely to
 * get subtly wrong, so they live somewhere a reviewer can diff at a glance.
 *
 * The value function is a weighted sum of terms on **deliberately different
 * scales** — this is the thing to understand before touching a weight:
 *
 * | term                     | raw range | × default weight | contribution |
 * |--------------------------|-----------|------------------|--------------|
 * | `tierValue`              | 10–100    | 1.00             | 10–100       |
 * | `relevance`              | 0–100     | 0.35             | 0–35         |
 * | `quality.score`          | 0–100     | 0.15             | 0–15         |
 * | `10 × unlockCount`       | 0–200     | 0.20             | 0–40         |
 * | `rosterMass`             | 0–~3      | 0.50             | 0–~1.5       |
 * | `threadMass`             | 0–~3      | 0.40             | 0–~1.2       |
 * | `selectedCharacterMatch` | 0–1       | 0.80             | 0–0.8        |
 * | `selectedThreadMatch`    | 0–1       | 0.80             | 0–0.8        |
 *
 * So the editorial tier dominates, relevance and unlock count shape the order
 * inside a tier, and the mass/match terms break ties between entries the first
 * four rank equally. That is the intended hierarchy: **the solver is a
 * time-budget optimiser, not a taste engine.**
 */

/** One coefficient per term of the value function (PLAN §3, step 4). */
export interface SolverWeights {
  /** On `solver.tierValue` — the editorial importance verdict. The anchor term. */
  tierValue: number;
  /** On `curation.relevance` (0–100) — continuous Doomsday relevance. */
  relevance: number;
  /** On `solver.rosterMass` — Σ link weight over confirmed Doomsday-roster characters. */
  rosterMass: number;
  /** On `solver.threadMass` — Σ link weight × the thread's own `targetWeight`. */
  threadMass: number;
  /** On the 0–1 match strength against the characters you filtered for. */
  characterMatch: number;
  /** On the 0–1 match strength against the threads you filtered for. */
  threadMatch: number;
  /** On `quality.score` (0–100). Deliberately small — see {@link QUALITY_WEIGHT_NOTE}. */
  quality: number;
  /** On `{@link UNLOCK_SCALE} × solver.unlockCount` — how much else this entry unlocks. */
  unlock: number;
}

/**
 * `unlockCount` is a small integer (0–~20) while every other integer term is a
 * 0–100 score, so it is scaled before weighting — PLAN §3 writes the term as
 * `0.20 × (10 × unlockCount)` and this is that 10.
 */
export const UNLOCK_SCALE = 10;

/**
 * Stand-in quality for an entry nobody has scored — no rating written and no
 * community votes, which on this road means it has not come out yet.
 *
 * The midpoint, not zero. Zero would actively penalise a thing for not having
 * been judged, pushing every unreleased title to the bottom of a plan whose
 * entire purpose is getting you ready for exactly those.
 */
export const UNRATED_QUALITY = 50;

/**
 * The one canonical key order. Used when the weights go into the plan's
 * deterministic hash, where "the order the object literal happened to be
 * written in" is not a thing another language can reproduce.
 */
export const WEIGHT_KEYS: readonly (keyof SolverWeights)[] = [
  "tierValue",
  "relevance",
  "rosterMass",
  "threadMass",
  "characterMatch",
  "threadMatch",
  "quality",
  "unlock",
];

/** PLAN §3, step 4, verbatim. */
export const DEFAULT_WEIGHTS: SolverWeights = {
  tierValue: 1.0,
  relevance: 0.35,
  rosterMass: 0.5,
  threadMass: 0.4,
  characterMatch: 0.8,
  threadMatch: 0.8,
  quality: 0.15,
  unlock: 0.2,
};

/**
 * The sentence that ships next to the quality slider (PLAN §3, step 4). It is
 * exported rather than typed into a component so the web drawer and the future
 * SwiftUI one say the same thing — and so nobody "helpfully" raises the weight
 * without reading why it is low.
 */
export const QUALITY_WEIGHT_NOTE =
  "Quality's weight is deliberately small: it nudges ties, it does not decide importance. " +
  "An essential-but-mediocre film is still essential.";

export const WEIGHT_MIN = 0;
/** Twice the largest default, which is enough to let a term dominate without letting it run away. */
export const WEIGHT_MAX = 2;
export const WEIGHT_STEP = 0.05;

/** Everything the advanced drawer needs to render one slider. */
export interface WeightControl {
  id: keyof SolverWeights;
  label: string;
  /** Sits under the slider. For `quality` this is {@link QUALITY_WEIGHT_NOTE}. */
  note: string;
  defaultValue: number;
  min: number;
  max: number;
  step: number;
}

/** Drawer order: the terms that decide the plan first, the tie-breakers last. */
export const WEIGHT_CONTROLS: readonly WeightControl[] = [
  {
    id: "tierValue",
    label: "Importance tier",
    note: "How much the editorial verdict, essential through skippable, drives the plan.",
    defaultValue: DEFAULT_WEIGHTS.tierValue,
    min: WEIGHT_MIN,
    max: WEIGHT_MAX,
    step: WEIGHT_STEP,
  },
  {
    id: "relevance",
    label: "{target} relevance",
    note: "The finer-grained 0–100 relevance score, which orders entries inside a tier.",
    defaultValue: DEFAULT_WEIGHTS.relevance,
    min: WEIGHT_MIN,
    max: WEIGHT_MAX,
    step: WEIGHT_STEP,
  },
  {
    id: "unlock",
    label: "Unlocks later entries",
    note: "Favours entries a lot of the road is built on, so prerequisites come early.",
    defaultValue: DEFAULT_WEIGHTS.unlock,
    min: WEIGHT_MIN,
    max: WEIGHT_MAX,
    step: WEIGHT_STEP,
  },
  {
    id: "rosterMass",
    label: "{target} roster",
    note: "Favours entries carrying characters on the announced {target} cast list.",
    defaultValue: DEFAULT_WEIGHTS.rosterMass,
    min: WEIGHT_MIN,
    max: WEIGHT_MAX,
    step: WEIGHT_STEP,
  },
  {
    id: "threadMass",
    label: "Story threads",
    note: "Favours entries that carry threads the finale is built on, weighted by each thread's own pull.",
    defaultValue: DEFAULT_WEIGHTS.threadMass,
    min: WEIGHT_MIN,
    max: WEIGHT_MAX,
    step: WEIGHT_STEP,
  },
  {
    id: "characterMatch",
    label: "Characters you picked",
    note: "Extra pull for entries matching the character chips you selected.",
    defaultValue: DEFAULT_WEIGHTS.characterMatch,
    min: WEIGHT_MIN,
    max: WEIGHT_MAX,
    step: WEIGHT_STEP,
  },
  {
    id: "threadMatch",
    label: "Threads you picked",
    note: "Extra pull for entries matching the thread chips you selected.",
    defaultValue: DEFAULT_WEIGHTS.threadMatch,
    min: WEIGHT_MIN,
    max: WEIGHT_MAX,
    step: WEIGHT_STEP,
  },
  {
    id: "quality",
    label: "Quality",
    note: QUALITY_WEIGHT_NOTE,
    defaultValue: DEFAULT_WEIGHTS.quality,
    min: WEIGHT_MIN,
    max: WEIGHT_MAX,
    step: WEIGHT_STEP,
  },
];

/**
 * Slack — PLAN §3, step 2. Capacity is discounted by this fraction because a
 * plan that books every free minute you claim to have is a plan you miss by
 * week two. 10% is roughly "one skipped evening a fortnight".
 */
export const DEFAULT_SLACK = 0.1;
export const SLACK_MIN = 0;
/** Above half your budget the plan stops being a plan. */
export const SLACK_MAX = 0.5;

const clamp = (value: number, min: number, max: number): number =>
  Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : min;

/** Fills in the defaults and clamps every coefficient into slider range. */
export function normalizeWeights(partial?: Partial<SolverWeights>): SolverWeights {
  const merged = { ...DEFAULT_WEIGHTS, ...partial };
  return {
    tierValue: clamp(merged.tierValue, WEIGHT_MIN, WEIGHT_MAX),
    relevance: clamp(merged.relevance, WEIGHT_MIN, WEIGHT_MAX),
    rosterMass: clamp(merged.rosterMass, WEIGHT_MIN, WEIGHT_MAX),
    threadMass: clamp(merged.threadMass, WEIGHT_MIN, WEIGHT_MAX),
    characterMatch: clamp(merged.characterMatch, WEIGHT_MIN, WEIGHT_MAX),
    threadMatch: clamp(merged.threadMatch, WEIGHT_MIN, WEIGHT_MAX),
    quality: clamp(merged.quality, WEIGHT_MIN, WEIGHT_MAX),
    unlock: clamp(merged.unlock, WEIGHT_MIN, WEIGHT_MAX),
  };
}

export function normalizeSlack(slack?: number): number {
  return slack === undefined ? DEFAULT_SLACK : clamp(slack, SLACK_MIN, SLACK_MAX);
}
